import { apiFetch } from '@app/utils/utils';
import {
  AdminCampaignSummary,
  AdminCampaignDetail,
  AdminCampaignRun,
  RecomputeMeasuredReachResult,
  RescheduleRunResult
} from '@app/Campaigns/types';

const BASE = '/api/admin/campaigns';

export async function getCampaigns(opts: {
  state?: string;
  q?: string;
  limit?: number;
  offset?: number;
} = {}): Promise<AdminCampaignSummary[]> {
  const params = new URLSearchParams();
  if (opts.state) params.set('state', opts.state);
  if (opts.q) params.set('q', opts.q);
  if (opts.limit != null) params.set('limit', String(opts.limit));
  if (opts.offset != null) params.set('offset', String(opts.offset));
  const qs = params.toString();
  const res = await apiFetch(`${BASE}${qs ? `?${qs}` : ''}`);
  if (!res.ok) throw new Error(`Failed to load campaigns (${res.status})`);
  return res.json();
}

export async function getCampaign(id: number): Promise<AdminCampaignDetail> {
  const res = await apiFetch(`${BASE}/${id}`);
  if (res.status === 404) throw new Error('Campaign not found');
  if (!res.ok) throw new Error(`Failed to load campaign (${res.status})`);
  return res.json();
}

export async function getPendingApprovalRuns(): Promise<AdminCampaignRun[]> {
  const res = await apiFetch(`${BASE}/runs/pending-approval`);
  if (!res.ok) throw new Error(`Failed to load approval queue (${res.status})`);
  return res.json();
}

/**
 * Move a run to a new 30-minute slot (ISO-8601 UTC, :00/:30 boundary; no minimum lead
 * time on this admin path). The backend keeps the assigned driver when they can cover
 * the new slot, otherwise picks another; either way the run drops back to
 * PENDING_APPROVAL for the driver to re-accept.
 */
export async function rescheduleRun(
  campaignId: number,
  runId: number,
  slotStart: string
): Promise<RescheduleRunResult> {
  const res = await apiFetch(`${BASE}/${campaignId}/runs/${runId}`, {
    method: 'PATCH',
    body: JSON.stringify({ slotStart })
  });
  if (res.status === 404) throw new Error('Run not found');
  if (!res.ok) {
    const text = await res.text();
    let reason: string | undefined;
    try {
      reason = JSON.parse(text)?.reason;
    } catch {
      /* plain-text error body */
    }
    throw new Error(
      (reason && RESCHEDULE_ERRORS[reason]) || reason || text || `Failed to reschedule run (${res.status})`
    );
  }
  return res.json();
}

/** Friendly messages for the backend's machine-readable reschedule reason codes. */
const RESCHEDULE_ERRORS: { [reason: string]: string } = {
  slot_in_past: 'Slot must be in the future.',
  no_eligible_driver: 'No driver is available for that slot.',
  run_not_reschedulable: 'Only pending, unstarted runs can be rescheduled.',
  slot_unavailable: 'That slot was just taken — try another time.'
};

/**
 * Re-trigger the measured-reach workflow for a campaign's completed runs. This is
 * fire-and-forget on the backend — the response reports what was started; results
 * land later once the worker writes them back (re-fetch the campaign to see them).
 */
export async function recomputeMeasuredReach(id: number): Promise<RecomputeMeasuredReachResult> {
  const res = await apiFetch(`${BASE}/${id}/measured-reach/recompute`, { method: 'POST' });
  if (res.status === 404) throw new Error('Campaign not found');
  if (!res.ok) throw new Error((await res.text()) || `Failed to recompute (${res.status})`);
  return res.json();
}
